import React from "react";
import { useTranslation } from "utilities/i18n";
import { roundAny } from "utilities/numberUtils";
import { parseISO, format } from "date-fns";
import i18n from "utilities/i18n";
import { useNetworkDiagnosticsQuery } from "generated/graphql";
import { LoadingIndicator } from "elements/LoadingIndicator";
import { NoResultsIndicatorAnimation } from "elements/NoResultsIndicator";
import { PolyPlusBadge } from "elements/PolyIcon";

type NetworkMetricType = "upload" | "download" | "latency" | "jitter" | "loss";
type NetworkPerformance = "good" | "fair" | "poor" | "unavailable";

/**
 * Calculate the "good", "fair", "poor" CSS classes for the various metrics.
 *
 * @return {string}
 */
const networkPerformanceGrade = (
  metric: number | null,
  type: NetworkMetricType
): NetworkPerformance => {
  if (null === metric) {
    return "unavailable";
  }

  const grades: Array<{
    type: NetworkMetricType;
    cssClass: NetworkPerformance;
    min?: number;
    max?: number;
  }> = [
    {
      type: "upload",
      cssClass: "poor",
      max: 1
    },
    {
      type: "upload",
      cssClass: "fair",
      min: 1,
      max: 3
    },
    {
      type: "upload",
      cssClass: "good",
      min: 3
    },
    {
      type: "download",
      cssClass: "poor",
      max: 1
    },
    {
      type: "download",
      cssClass: "fair",
      min: 1,
      max: 3
    },
    {
      type: "download",
      cssClass: "good",
      min: 3
    },
    {
      type: "latency",
      cssClass: "good",
      max: 50
    },
    {
      type: "latency",
      cssClass: "fair",
      min: 50,
      max: 150
    },
    {
      type: "latency",
      cssClass: "poor",
      min: 150
    },
    {
      type: "jitter",
      cssClass: "poor",
      min: 30
    },
    {
      type: "jitter",
      cssClass: "good",
      max: 30
    },
    {
      type: "loss",
      cssClass: "poor",
      min: 1
    },
    {
      type: "loss",
      cssClass: "good",
      max: 1
    }
  ];

  let relevant = grades.filter(grade => grade.type === type);

  relevant = relevant.filter(grade => {
    const min = "undefined" !== typeof grade.min ? grade.min : false;
    const max = "undefined" !== typeof grade.max ? grade.max : false;

    if (false !== min && false !== max) {
      return min <= metric && max >= metric;
    }

    if (false !== min) {
      return min <= metric;
    }

    if (false !== max) {
      return max >= metric;
    }

    return false;
  });

  if (!relevant.length || relevant.length > 1) {
    return "good";
  }

  return relevant[0].cssClass;
};

const networkPerformance = (metric: number | null, type: NetworkMetricType) => {
  const grades: { [key in NetworkPerformance]: string } = {
    unavailable: i18n.t("UNAVAILABLE"),
    good: i18n.t("GOOD"),
    fair: i18n.t("FAIR"),
    poor: i18n.t("POOR")
  };

  const performanceGrade = networkPerformanceGrade(metric, type);

  return grades[performanceGrade];
};

const NetworkStatistic: React.FC<{
  type: NetworkMetricType;
  label: string;
  metric: number | null;
  units: string;
}> = ({ type, label, metric, units }) => {
  const performance =
    null === metric ? "unavailable" : networkPerformanceGrade(metric, type);
  const displayMetric = null === metric ? <>&mdash;</> : roundAny(metric);
  const displayUnits = null === metric ? "" : units;
  const classes = ["col-2", "metric-" + type, performance].join(" ");

  return (
    <div className={classes}>
      <label>{label}</label>
      <div className="metric">
        <span className="statistic">{displayMetric}</span>
        <span className="units">{displayUnits}</span>
      </div>
      <div className="grade">{networkPerformance(metric, type)}</div>
    </div>
  );
};

export const UserNetworkDiagnostics: React.FC<{ deviceId: string | null }> = ({
  deviceId
}) => {
  const { t } = useTranslation();
  const { data, fetching } = useNetworkDiagnosticsQuery({
    variables: {
      id: deviceId ?? ""
    },
    pause: !deviceId
  });

  if (!deviceId) {
    return (
      <div className="card p-1 mb-4">
        <NoResultsIndicatorAnimation className="mt-2 inline">
          <p className="font-sm ph-4">
            {t("NO_CONNECTED_DESKTOP_APP")}
            <br />
            {t("NO_CONNECTED_DESKTOP_APP_INSTRUCTIONS")}
          </p>
        </NoResultsIndicatorAnimation>
        <div className="text-right mt-1 mb-n5">
          <PolyPlusBadge />
        </div>
      </div>
    );
  }

  if (fetching) {
    return (
      <div className="card p-4">
        <LoadingIndicator className="inline m-3" />
      </div>
    );
  }

  if (!data?.device?.bandwidth || !data?.device?.bandwidth?.endTime) {
    return (
      <div className="card p-1 mb-4">
        <NoResultsIndicatorAnimation className="mt-2 inline">
          <p className="font-sm ph-4">
            {t("NO_NETWORK_TEST_RESULTS")}
            <br />
            {t("NO_NETWORK_TEST_RESULTS_INSTRUCTIONS")}
          </p>
        </NoResultsIndicatorAnimation>
        <div className="text-right mt-1 mb-n5">
          <PolyPlusBadge />
        </div>
      </div>
    );
  }

  const {
    endTime,
    downloadMbps,
    uploadMbps,
    pingLatencyMs,
    pingJitterMs,
    pingLossPercent
  } = data.device.bandwidth;

  const dateDisplay = format(parseISO(endTime), "EEEE, MMMM d yyyy - h:mmaaa");

  return (
    <div className="card p-1 mb-5">
      <h6>{t("NETWORK_DIAGNOSTIC_TESTING")}</h6>
      <div className="mid-gray mt-14">{dateDisplay}</div>
      <div className="grid grid-10 statistics mt-1">
        <NetworkStatistic
          type="download"
          label={t("DOWNLOAD")}
          metric={downloadMbps}
          units={t("MBPS")}
        />
        <NetworkStatistic
          type="upload"
          label={t("UPLOAD")}
          metric={uploadMbps}
          units={t("MBPS")}
        />
        <NetworkStatistic
          type="latency"
          label={t("LATENCY")}
          metric={pingLatencyMs}
          units={t("MS")}
        />
        <NetworkStatistic
          type="jitter"
          label={t("JITTER")}
          metric={pingJitterMs}
          units={t("MS")}
        />
        <NetworkStatistic
          type="loss"
          label={t("PACKET_LOSS")}
          metric={pingLossPercent}
          units="%"
        />
      </div>
      <div className="text-right mt-1 mb-n5">
        <PolyPlusBadge />
      </div>
    </div>
  );
};
